import { useState } from "react";
import { AutoResizeTextarea } from "@/components/ui/AutoResizeTextarea";
import { SignatureCanvas } from "@/features/timesheetFlow/components/SignatureCanvas";
import {
  TimesheetFlowFooter,
  TimesheetFlowFooterButton,
} from "@/features/timesheetFlow/components/TimesheetFlowFooter";
import { submitMuendlichBeauftragt } from "@/features/timesheetFlow/timesheetFlowApi";
import { formatTimesheetOrt } from "@/features/timesheetFlow/lib/timesheetProjectPicker";
import type { TimesheetProjectPreset } from "@/features/timesheetFlow/timesheetFlowTypes";
import { toast } from "@/lib/toast";

/** Standardtext für die Versicherung des Monteurs. */
export const MUENDLICH_BEAUFTRAGT_STATEMENT_TEXT =
  "Hiermit versichere ich als Monteur, dass der Auftraggeber den vorliegenden Auftrag bei mir mündlich bestellt hat. Mit meiner Unterschrift bestätige ich diese mündliche Beauftragung.";

export function MuendlichBeauftragtScreen({
  project,
  onDone,
  onBack,
}: {
  project: TimesheetProjectPreset;
  onDone: () => void;
  onBack: () => void;
}) {
  const [notiz, setNotiz] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = hasStrokes && signature;

  const handleSubmit = async () => {
    if (!canSubmit || !signature) return;
    setBusy(true);
    setError(null);
    try {
      await submitMuendlichBeauftragt(project.id, {
        statementText: MUENDLICH_BEAUFTRAGT_STATEMENT_TEXT,
        imageDataUrl: signature,
        notiz: notiz.trim() || null,
      });
      toast.success("Mündliche Beauftragung gespeichert. Projekt ist beauftragt.");
      onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-separator bg-fill-quaternary/50 px-3 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
          Versicherung
        </p>
        <p className="mt-1 text-sm text-label">{MUENDLICH_BEAUFTRAGT_STATEMENT_TEXT}</p>
        {(project.auftragsnummer || project.kunde) && (
          <p className="mt-2 text-xs text-label-secondary">
            {[
              project.auftragsnummer,
              project.kunde,
              formatTimesheetOrt(project.ort, project.ort_zusatz),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
      </div>

      <label className="block space-y-1">
        <span className="text-sm font-medium text-label">Notiz (optional)</span>
        <AutoResizeTextarea
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          disabled={busy}
          placeholder="z. B. Telefonat mit Herrn Müller, Bestellung der Montage…"
          className="eh-input w-full"
          minRows={3}
        />
      </label>

      <div>
        <p className="mb-2 text-sm font-medium text-label">Unterschrift Monteur</p>
        <p className="mb-2 text-xs text-label-secondary">
          Mit Finger oder Maus unterschreiben — Sie versichern die mündliche Bestellung durch den
          Auftraggeber.
        </p>
        <SignatureCanvas
          onChange={(strokes, dataUrl) => {
            setHasStrokes(strokes);
            setSignature(dataUrl);
          }}
        />
      </div>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      <TimesheetFlowFooter>
        <TimesheetFlowFooterButton type="button" variant="secondary" onClick={onBack} disabled={busy}>
          Zurück
        </TimesheetFlowFooterButton>
        <TimesheetFlowFooterButton
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit || busy}
        >
          Mündlich beauftragt speichern
        </TimesheetFlowFooterButton>
      </TimesheetFlowFooter>
    </div>
  );
}
